import { Form, Formik, useFormikContext } from "formik";
import { FC, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import Search from "web/Layout/Common/Search";
import __ from "web/Layout/Translations";

import { getCodes } from "web/features/codes/codesThunks";
import { FilterValues } from "web/features/codes/utils/helpers";

import { pageName } from "web/constants/toolbar";

import classes from "./filters.scss";

interface ICustomerCodeListFiltersDesktopProps {
  filters: { label: string; value: string; count: number }[];
  name: string;
  type: string;
}

const codeNameParam = "codeName";
const TIMEOUT = 500;

interface IFilterButtonsProps {
  name: string;
  filters: { label: string; value: string; count: number }[];
}

const FilterButtons: FC<IFilterButtonsProps> = ({ name, filters }) => {
  const { values, setFieldValue, submitForm } = useFormikContext<{
    [key: string]: string;
  }>();

  const handleClick = useCallback(
    (value: string) => {
      setFieldValue(name, value);
      submitForm();
    },
    [name, setFieldValue, submitForm]
  );

  return (
    <>
      {filters.map(({ label, value }) => {
        const isSelected = values[name] === value;
        return (
          <div
            className={classes.item}
            key={value}
            data-t1="code_list_filters_item"
            data-t2={label}
          >
            <button
              type="button"
              className={`${classes.filterButton} ${
                isSelected ? classes.filterButtonSelected : ""
              }`}
              onClick={() => handleClick(value)}
              data-t1={`${value}_field`}
              aria-pressed={isSelected}
              disabled={isSelected}
            >
              <span
                className={classes.filterButtonLabel}
                data-t1={`${value}_label`}
              >
                {label}
              </span>
            </button>
          </div>
        );
      })}
    </>
  );
};

const CustomerCodeListFiltersDesktop: FC<
  ICustomerCodeListFiltersDesktopProps
> = ({ name, filters, type }) => {
  const { pathname, search } = useLocation();
  const { push } = useHistory();
  const params = new URLSearchParams(search);
  const dispatch = useDispatch();
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentNameSearch = params.get(codeNameParam) ?? "";

  const initialValues = {
    [name]: type,
  };
  const onSubmit = useCallback(
    (values: { [key: string]: FilterValues | string }) => {
      params.delete(pageName);
      params.set(name, values[name]);
      push(`${pathname}?${params.toString()}`);
      dispatch(
        getCodes({
          page: 1,
          filterValue: values[name] as FilterValues,
          searchName: currentNameSearch,
        })
      );
    },
    [name, params, pathname, push, dispatch, currentNameSearch]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
      timeoutId.current = setTimeout(() => {
        const nextParams = new URLSearchParams(search);
        nextParams.delete(pageName);
        if (!value) {
          nextParams.delete(codeNameParam);
        } else {
          nextParams.set(codeNameParam, value);
        }
        push(`${pathname}?${nextParams.toString()}`);
      }, TIMEOUT);
    },
    [pathname, push, search]
  );

  useEffect(() => {
    return () => {
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, []);

  return isArrayHasItems(filters) ? (
    <div className={classes.root}>
      <div className={classes.row}>
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          <Form className={classes.filtersForm}>
            <FilterButtons name={name} filters={filters} />
          </Form>
        </Formik>
        <div className={classes.spacer} />
        <Search
          className={classes.search}
          placeholder={__("Szukaj po nazwie")}
          initialValue={currentNameSearch}
          onChange={(value: unknown) => handleSearchChange(value as string)}
          hasRandomId={false}
          dataT1="code_list_search"
          ariaLabel={__("Szukaj po nazwie")}
        />
      </div>
    </div>
  ) : null;
};

export default CustomerCodeListFiltersDesktop;
